import { useTranslation } from "react-i18next";
import { AD_TYPES, ALGORITHM_OPTIONS, type AlgorithmValue } from "../../../components/core/blocks/AdPlan/constants";
import { SliderWithMarks } from "../../../components/fields/base/Slider";
import { Flex, Wrap } from "../../../../styled-system/jsx";
import { css } from "../../../../styled-system/css";
import { Select } from "../../../components/fields/base/Select";
import { useAppSelector } from "../../../redux/hooks";
import { useResolvedLabels } from "../../../i18n/useResolvedLabels";

interface BulkAlgoFormData {
  algorithm: AlgorithmValue;
  salesVolumeControl: number;
  adType: number;
}

type Props = {
  formData: BulkAlgoFormData;
  setFormData: (data: BulkAlgoFormData) => void;
};

const BulkAdPlanAlgoForm = ({ formData, setFormData }: Props) => {
  const { t } = useTranslation(["advertising"]);
  const { currentChannel } = useAppSelector((state) => state.channel);

  const adTypeOptions = [
    AD_TYPES.SPONSORED_PRODUCT,
    AD_TYPES.SPONSORED_PRODUCT_AUTO,
    AD_TYPES.SPONSORED_BRAND_V2,
    AD_TYPES.SPONSORED_DISPLAY_V2,
  ];

  const algoOptions: (typeof ALGORITHM_OPTIONS)[keyof typeof ALGORITHM_OPTIONS][] = [
    ALGORITHM_OPTIONS.ROAS_BOOST,
    ALGORITHM_OPTIONS.GROWTH_MAX,
    ALGORITHM_OPTIONS.RULES_ENGINE_ONLY,
    ALGORITHM_OPTIONS.SMART_SPEND,
  ];

  const onChange = (key: string, value: number | string | null) => {
    const updatedFormData = { ...formData, [key]: value };
    setFormData(updatedFormData);
  };

  if (
    [AD_TYPES.SPONSORED_PRODUCT.value, AD_TYPES.SPONSORED_PRODUCT_AUTO.value].includes(formData.adType) &&
    currentChannel === "amazon"
  ) {
    algoOptions.push(ALGORITHM_OPTIONS.PLACEMENT_MAX);
  } else {
    algoOptions.filter((o) => o.value !== ALGORITHM_OPTIONS.PLACEMENT_MAX.value);
    if (formData.algorithm === ALGORITHM_OPTIONS.PLACEMENT_MAX.value) {
      onChange("algorithm", ALGORITHM_OPTIONS.GROWTH_MAX.value);
    }
  }

  const resolvedAdTypeOptions = useResolvedLabels(adTypeOptions, ["advertising"]);
  const resolvedAlgoOptions = useResolvedLabels(algoOptions, ["advertising"]);

  return (
    <form
      onSubmit={(e) => e.preventDefault()}
      className={css({ display: "flex", flexDirection: "column", alignItems: "center" })}
    >
      <p className={css({ padding: "large", textAlign: "center" })}>
        {t(($) => $.advertising["Update the bidding algorithm for the selected media plans."])}
      </p>
      <Wrap gap="large" width="100%">
        <Select
          label={t(($) => $.advertising["Media Plan Type"])}
          onSelectionChange={(value) => {
            onChange("adType", value);
          }}
          selectedKey={formData.adType}
          items={resolvedAdTypeOptions}
        />
        <Select
          label={t(($) => $.advertising["Algorithm"])}
          onSelectionChange={(value) => {
            onChange("algorithm", value);
          }}
          selectedKey={formData.algorithm}
          items={resolvedAlgoOptions}
        />
      </Wrap>

      {formData.algorithm === ALGORITHM_OPTIONS.GROWTH_MAX.value ?
        <Flex className={css({ width: "100%", margin: "auto", paddingTop: "medium" })} justifyContent="center">
          <SliderWithMarks
            aria-label={t(($) => $.advertising["Sales volume control"])}
            step={1}
            minValue={0}
            maxValue={9}
            marks={[
              { value: 0, label: t(($) => $.advertising["Increase Margin"]) },
              { value: 1, label: t(($) => $.advertising["Optimal Profit"]) },
              { value: 2 },
              { value: 3 },
              { value: 4 },
              { value: 5 },
              { value: 6 },
              { value: 7 },
              { value: 8 },
              { value: 9, label: t(($) => $.advertising["Max Volume"]) },
            ]}
            value={formData.salesVolumeControl}
            onChange={(value) => {
              onChange("salesVolumeControl", value);
            }}
          />
        </Flex>
      : null}
    </form>
  );
};

export default BulkAdPlanAlgoForm;
